import { useEffect, useRef, useState } from 'react';
import type { SortSpec } from '@/domain/shared/sortOptions';

export function useSortedItemsOnce<T>(args: {
  scopeKey: string;
  items: T[];
  enabled: boolean;
  activeSort: SortSpec;
  sort: (items: T[], sort: SortSpec) => T[];
  reconcile: (prev: T[], next: T[]) => T[];
}) {
  const { scopeKey, items, enabled, activeSort } = args;

  const didInitSortRef = useRef(false);
  const lastScopeRef = useRef('');

  const sortRef = useRef(args.sort);
  const reconcileRef = useRef(args.reconcile);

  // keep latest implementations without making the main effect depend on them
  useEffect(() => {
    sortRef.current = args.sort;
    reconcileRef.current = args.reconcile;
  }, [args.sort, args.reconcile]);

  const [sortedItems, setSortedItems] = useState<T[]>([]);

  const resort = () => {
    didInitSortRef.current = true;
    setSortedItems(sortRef.current(items, activeSort));
  };

  useEffect(() => {
    if (lastScopeRef.current !== scopeKey) {
      lastScopeRef.current = scopeKey;
      didInitSortRef.current = false;
      setSortedItems([]);
    }

    // create mode / not fetched yet: mirror items
    if (!enabled) {
      setSortedItems(items);
      return;
    }

    // first time after enabled: sort once
    if (!didInitSortRef.current) {
      didInitSortRef.current = true;
      setSortedItems(sortRef.current(items, activeSort));
      return;
    }

    // after that: reconcile only
    setSortedItems((prev) => reconcileRef.current(prev, items));
  }, [scopeKey, enabled, items, activeSort]);

  return { sortedItems, setSortedItems, resort };
}
